import { useEffect } from 'react';

export default function useReadingChapters(containerRef, content) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !content) return;
    const chapters = Array.from(container.querySelectorAll('.bible-reading-chapter'));
    if (!chapters.length) return;
    let frame;
    let measurementFrame;
    let disposed = false;
    const measure = () => {
      if (disposed) return;
      cancelAnimationFrame(frame);
      cancelAnimationFrame(measurementFrame);
      // ResizeObserver callbacks must not change layout during delivery.
      // Defer even the removal of content-visibility to the next frame.
      frame = requestAnimationFrame(() => {
        if (disposed) return;
        chapters.forEach((chapter) => chapter.classList.remove('bible-reading-chapter--measured'));
        measurementFrame = requestAnimationFrame(() => {
          if (disposed) return;
          // Read all heights before writing any styles.
          const heights = chapters.map((chapter) => chapter.getBoundingClientRect().height);
          chapters.forEach((chapter, index) => {
            chapter.style.containIntrinsicBlockSize = `auto ${heights[index]}px`;
            chapter.classList.add('bible-reading-chapter--measured');
          });
        });
      });
    };
    measure();
    // Mobile browser toolbars resize the viewport height during scrolling.
    // Only a change to the reader's width requires laying out every chapter again.
    let width = container.getBoundingClientRect().width;
    const widthChanged = () => {
      const nextWidth = container.getBoundingClientRect().width;
      if (nextWidth > 0 && Math.abs(nextWidth - width) >= 0.5) {
        width = nextWidth;
        measure();
      }
    };
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(widthChanged);
    resize?.observe(container);
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        target.classList.toggle('bible-reading-chapter--nearby', isIntersecting);
      });
    }, { rootMargin: '800px 0px' });
    chapters.forEach((chapter) => observer?.observe(chapter));
    const fontsChanged = () => { if (!disposed) measure(); };
    document.fonts?.addEventListener('loadingdone', fontsChanged);
    window.addEventListener('resize', widthChanged);
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      cancelAnimationFrame(measurementFrame);
      resize?.disconnect();
      observer?.disconnect();
      document.fonts?.removeEventListener('loadingdone', fontsChanged);
      window.removeEventListener('resize', widthChanged);
      chapters.forEach((chapter) => {
        chapter.classList.remove('bible-reading-chapter--measured', 'bible-reading-chapter--nearby');
        chapter.style.removeProperty('contain-intrinsic-block-size');
      });
    };
  }, [containerRef, content]);
}
